import { useEffect, useMemo, useState } from 'react';
import { useFormContext, Controller } from 'react-hook-form';
import {
  requiredSelect,
  fieldRequired,
  requiredDatePicker,
  Select,
} from '../../../core/formValidation';
import { TaskType } from '../../tasks/models';
import { useIntl } from 'react-intl';
import { Task } from '../../tasks/models';
import { useDispatch, useSelector } from 'react-redux';
import { selectTaskLocations, selectTaskPrefixes } from '../../tasks/reducer';
import { createIntervalDescription } from '../../tasks/helpers';
import WeeklyRepeatOptions from '../../tasks/components/WeeklyRepeatOptions';
import HourlyRepeatOptions from '../../tasks/components/HourlyRepeatOptions';
import DateTimePicker from '../../../core/components/DateTimePicker';
import { getTaskLocations, getTaskPrefixes } from '../../tasks/actions';
import { Interval } from '../../tasks/models';
import { selectSiteInteriorDetailRadius } from '../../settings/reducer';
import { selectSite } from '../../../store/authReducer';
import { selectEmployees } from '../../employees/reducer';
import Employee from '../../employees/models';
import CustomSelectInput from '../../../core/components/CustomSelectInput';
import { selectAssetGroups, selectAssetItems } from '../../assets/reducer';
import { fetchAssetGroups, fetchAssetItems } from '../../assets/actions';
import { getGroupDisplayName, getItemDisplayName } from '../../assets/helpers';

interface TaskDetailsTabProps {
  task?: Task | null;
  editing?: boolean;
  type?: number | null;
}

interface SelectOption {
  value: any;
  text: string;
}

export const TaskDetailsTab = ({ task, editing = false, type = null }: TaskDetailsTabProps) => {
  const intl = useIntl();
  const dispatch = useDispatch();
  const form = useFormContext();
  const formValues = form.watch();
  const taskLocations = useSelector(selectTaskLocations);
  const taskPrefixes = useSelector(selectTaskPrefixes);
  const interiorRadius = useSelector(selectSiteInteriorDetailRadius);
  const employees = useSelector(selectEmployees);
  const siteId = useSelector(selectSite);
  const groups = useSelector(selectAssetGroups);
  const items = useSelector(selectAssetItems);

  const intervalConfig = {
    interval: editing
      ? formValues.interval
      : typeof task?.interval === 'string'
      ? (Interval as Record<string, number>)[task.interval] ?? task.interval
      : task?.interval,
    intervalCount: editing ? formValues.intervalCount : task?.intervalCount,
    weeklySchedule: editing ? formValues.weeklySchedule : task?.weeklySchedule,
    monthlySameWeekday: editing ? formValues.monthlySameWeekday : task?.monthlySameWeekday,
    start: editing ? formValues.start : task?.start,
    startingCarCount: editing ? formValues.startingCarCount : task?.startingCarCount,
    intervalCarCount: editing ? formValues.intervalCarCount : task?.intervalCarCount,
  };

  const intervalOptions = [
    { value: Interval.CarCount, text: 'Car Count' },
    { value: Interval.Once, text: 'Once' },
    { value: Interval.Daily, text: 'Daily' },
    { value: Interval.Weekly, text: 'Weekly' },
    { value: Interval.Monthly, text: 'Monthly' },
    { value: Interval.Yearly, text: 'Yearly' },
    { value: Interval.Hourly, text: 'Hourly' },
  ];

  const [typeOptions, setTypeOptions] = useState<{ value: number; text: string }[]>([]);

  useEffect(() => {
    const options: { value: number; text: string }[] = [
      { value: TaskType.Maintenance, text: 'Maintenance' },
      { value: TaskType.Project, text: 'Work Order' },
      { value: TaskType.OpenChecklist, text: 'Open Checklist' },
      { value: TaskType.ClosingChecklist, text: 'Closing Checklist' },
      {
        value: TaskType.PreventativeMaintenance,
        text: 'Preventative Maintenance',
      },
      { value: TaskType.CleaningList, text: 'Cleaning List' },
      { value: TaskType.HourlyInspection, text: 'Hourly Inspection' },
      { value: TaskType.ExpressInterior, text: 'Express Interior' },
    ];

    if (interiorRadius != null && interiorRadius <= 0) {
      options.pop();
    }

    setTypeOptions(options);
  }, [siteId]);

  const priorityOptions = [
    { value: 0, text: 'Low' },
    { value: 1, text: 'Medium' },
    { value: 2, text: 'High' },
    { value: 3, text: 'Urgent' },
  ];

  const formatLocationOptions = (locations: any[]) => {
    if (!locations) return [];
    const locationOptions = locations.map((location: any) => ({
      text: location.message,
      value: location.type,
    }));
    locationOptions.unshift({ text: '', value: null });
    return locationOptions;
  };

  const formatPrefixOptions = (prefixes: any[]) => {
    if (!prefixes) return [];
    const prefixOptions = prefixes.map((prefix: any) => ({
      text: prefix.message,
      value: prefix.type,
    }));
    prefixOptions.unshift({ text: '', value: null });
    return prefixOptions;
  };

  const formatEmployeeOptions = (employeeList: Employee[]) => {
    if (!employeeList) return [];
    const employeeOptions = employeeList.map((emp: Employee) => ({
      text: emp.firstName + ' ' + emp.lastName,
      value: emp.id,
    }));
    employeeOptions.unshift({ text: 'Select an employee...', value: 0 });
    return employeeOptions;
  };

  const getLocationFromId = (locationId: number) => {
    const locationString = taskLocations.find(
      (taskLocation: any) => taskLocation.type === locationId
    );
    return locationString?.message ?? intl.formatMessage({ id: 'notAdded' });
  };

  const getValueFromId = (value: number, options: SelectOption[]) => {
    const item = options.find((o: any) => o.value == value);
    return item?.text ?? intl.formatMessage({ id: 'notAdded' });
  };

  const formatNumberValue = (n: number, singular: string, plural: string) => {
    if (n && n > 0) {
      return `${n} ${n === 1 ? singular : plural}`;
    }
    return intl.formatMessage({ id: 'notAdded' });
  };

  const formatTimeString = (hours: number) => {
    hours = Number(hours);
    if (hours >= 1) {
      return formatNumberValue(
        hours,
        intl.formatMessage({ id: 'hourSingular' }),
        intl.formatMessage({ id: 'hourPlural' })
      );
    } else {
      const minutes = Math.floor(60 * hours);
      return formatNumberValue(
        minutes,
        intl.formatMessage({ id: 'minuteSingular' }),
        intl.formatMessage({ id: 'minutePlural' })
      );
    }
  };

  const capacityOptions = (employeeCount: number) => {
    const options = [{ value: '', text: '' }];
    for (let e = 1; e <= employeeCount; e++) {
      const option = {
        value: String(e),
        text: formatNumberValue(
          e,
          intl.formatMessage({ id: 'employeeSingular' }),
          intl.formatMessage({ id: 'employeePlural' })
        ),
      };
      options.push(option);
    }
    return options;
  };

  const groupOptions = useMemo(
    () =>
      groups
        .filter((g: any) => !g.archivedAt)
        .map((g: any) => ({ value: `group:${g.id}`, label: getGroupDisplayName(g) })),
    [groups]
  );

  const itemOptions = useMemo(
    () =>
      items
        .filter((i: any) => !i.archivedAt)
        .map((i: any) => ({ value: `item:${i.id}`, label: getItemDisplayName(i) })),
    [items]
  );

  const assetSelectOptions: any = [
    { label: 'Groups', options: groupOptions },
    { label: 'Items', options: itemOptions },
  ];

  const assetSelectValue = useMemo(() => {
    const gId = formValues.assetGroupId;
    const iId = formValues.assetItemId;
    if (gId != null) {
      const group = groups.find((g: any) => g.id === gId);
      if (group) return { value: `group:${gId}`, label: getGroupDisplayName(group) };
    }
    if (iId != null) {
      const item = items.find((i: any) => i.id === iId);
      if (item) return { value: `item:${iId}`, label: getItemDisplayName(item) };
    }
    return null;
  }, [formValues.assetGroupId, formValues.assetItemId, groups, items]);

  const handleAssetChange = (option: any) => {
    if (!option) {
      form.setValue('assetGroupId', null);
      form.setValue('assetItemId', null);
      return;
    }
    const [scopeType, idStr] = (option.value as string).split(':');
    if (scopeType === 'group') {
      form.setValue('assetGroupId', Number(idStr));
      form.setValue('assetItemId', null);
    } else {
      form.setValue('assetGroupId', null);
      form.setValue('assetItemId', Number(idStr));
    }
  };

  const assetDisplayLabel = useMemo(() => {
    const gId = (task as any)?.assetGroupId;
    const iId = (task as any)?.assetItemId;
    if (gId != null) {
      const group = groups.find((g: any) => g.id === gId);
      return group ? getGroupDisplayName(group) : `Group #${gId}`;
    }
    if (iId != null) {
      const item = items.find((i: any) => i.id === iId);
      return item ? getItemDisplayName(item) : `Item #${iId}`;
    }
    return null;
  }, [task, groups, items]);

  useEffect(() => {
    dispatch(getTaskLocations());
    dispatch(getTaskPrefixes());
    if (!groups.length) dispatch(fetchAssetGroups());
    if (!items.length) dispatch(fetchAssetItems());
  }, []);

  return (
    <div className="task-details-tab">
      <div className="form-group padded">
        <label htmlFor="type">{intl.formatMessage({ id: 'type' })}</label>
        {editing ? (
          <Controller
            name="type"
            rules={{ required: true }}
            render={({ field: { value, onChange } }) =>
              requiredSelect({
                input: { value, onChange: (e: any) => onChange(Number(e?.target?.value ?? e)) },
                id: 'type',
                options: typeOptions,
                disabled: type !== null,
              })
            }
          />
        ) : (
          <p>{isNaN(task?.type) ? task?.type : getValueFromId(task?.type, typeOptions)}</p>
        )}
      </div>
      {formValues.type == TaskType.Project && (
        <>
          <div className="form-group padded">
            <label htmlFor="type">{intl.formatMessage({ id: 'taskPrefix' })}</label>
            {editing ? (
              <Controller
                name="prefixId"
                rules={{ required: true }}
                render={({ field: { value, onChange } }) =>
                  requiredSelect({
                    input: {
                      value,
                      onChange: (e: any) => onChange(Number(e?.target?.value ?? e)),
                    },
                    id: 'type',
                    options: formatPrefixOptions(taskPrefixes),
                  })
                }
              />
            ) : (
              <p>
                {isNaN(task?.prefixId ?? 0)
                  ? task?.prefixId
                  : getValueFromId(task?.prefixId ?? 0, formatPrefixOptions(taskPrefixes))}
              </p>
            )}
          </div>
          <div className="form-group padded">
            <label htmlFor="type">{intl.formatMessage({ id: 'assignee' })}</label>
            {editing ? (
              <Controller
                name="employeeId"
                rules={{ required: false }}
                render={({ field: { value, onChange } }) =>
                  Select({
                    input: {
                      value,
                      onChange: (e: any) => onChange(Number(e?.target?.value ?? e)),
                    },
                    id: 'type',
                    options: formatEmployeeOptions(employees),
                    children: null,
                  })
                }
              />
            ) : (
              <p>{getValueFromId(task?.employeeId, formatEmployeeOptions(employees))}</p>
            )}
          </div>
        </>
      )}
      <div className="form-group padded">
        <label htmlFor="locationId">{intl.formatMessage({ id: 'location' })}</label>
        {editing ? (
          <Controller
            name="locationId"
            rules={{ required: true }}
            render={({ field: { value, onChange } }) =>
              requiredSelect({
                input: {
                  value,
                  onChange: (e: any) => {
                    const raw = e?.target?.value ?? e;
                    onChange(raw ? Number(raw) : null);
                  },
                },
                id: 'locationId',
                options: formatLocationOptions(taskLocations),
              })
            }
          />
        ) : (
          <p>{getLocationFromId(task?.locationId ?? 0)}</p>
        )}
      </div>
      <div className="form-group padded">
        <label htmlFor="interval">{intl.formatMessage({ id: 'interval' })}</label>
        {editing ? (
          <Controller
            name="interval"
            rules={{ required: true }}
            render={({ field: { value, onChange } }) =>
              requiredSelect({
                input: {
                  value,
                  onChange: (e: any) => {
                    const raw = e?.target?.value ?? e;
                    onChange(raw ? Number(raw) : null);
                  },
                },
                id: 'interval',
                options: intervalOptions,
                disabled: type == TaskType.Project,
              })
            }
          />
        ) : (
          <p>{createIntervalDescription(intervalConfig, intl.formatMessage)}</p>
        )}
      </div>
      {editing && (
        <>
          {formValues.interval == Interval.CarCount ? (
            <div className="form-row half no-margin">
              <div className="form-group padded">
                <label>Starting car count</label>
                <input
                  {...form.register('startingCarCount', { required: true })}
                  className="form-control"
                  type="number"
                  min="1"
                  inputMode="numeric"
                />
              </div>
              <div className="form-group padded">
                <label>Repeat interval</label>
                <input
                  {...form.register('intervalCarCount', { required: true })}
                  className="form-control"
                  type="number"
                  min="1"
                  inputMode="numeric"
                />
              </div>
            </div>
          ) : (
            <div
              className={`form-row no-margin${
                formValues.interval != Interval.Once ? ' half' : ' full'
              }`}
            >
              <div className="form-group padded">
                <label>{formValues.interval != Interval.Once ? 'Start' : 'Deadline'}</label>
                <Controller
                  name="start"
                  rules={{ required: true }}
                  render={({ field }) =>
                    requiredDatePicker({
                      input: field,
                      id: 'start',
                      placeholderText: 'Choose date',
                      showToday: true,
                    })
                  }
                />
              </div>
              {formValues.interval != Interval.Once && (
                <div className="form-group padded">
                  <label>End</label>
                  <Controller
                    name="end"
                    render={({ field }) => (
                      <DateTimePicker
                        input={field}
                        placeholderText="Choose date"
                        showToday={false}
                      />
                    )}
                  />
                </div>
              )}
            </div>
          )}
          <div className="form-row no-margin full">
            {formValues.interval === Interval.Weekly && (
              <Controller
                name="weeklySchedule"
                render={({ field }) => <WeeklyRepeatOptions input={field} />}
              />
            )}
            {formValues.interval === Interval.Hourly && (
              <Controller
                name="hourlySchedule"
                render={({ field }) => (
                  <HourlyRepeatOptions input={field} hours={field.value || []} />
                )}
              />
            )}
          </div>
        </>
      )}
      <div className="form-group padded">
        <label htmlFor="estimatedTime">
          {intl.formatMessage({ id: 'estimatedTime' })}
          {editing ? ' (in hours)' : ''}
        </label>
        {editing ? (
          <input
            {...form.register('estimatedTime')}
            className="form-control"
            type="number"
            min="0.25"
            step={0.25}
            inputMode="numeric"
          />
        ) : (
          <p>{formatTimeString(task?.estimatedTime)}</p>
        )}
      </div>
      <div className="form-group padded">
        <label htmlFor="capacity">{intl.formatMessage({ id: 'estimatedCapacity' })}</label>
        {editing ? (
          <Controller
            name="capacity"
            defaultValue={''}
            render={({ field }) => (
              <Select input={field} options={capacityOptions(10)}>
                {''}
              </Select>
            )}
          />
        ) : (
          <p>
            {formatNumberValue(
              task?.capacity,
              intl.formatMessage({ id: 'employeeSingular' }),
              intl.formatMessage({ id: 'employeePlural' })
            )}
          </p>
        )}
      </div>
      <div className="form-group padded">
        <label htmlFor="priority">{intl.formatMessage({ id: 'priority' })}</label>
        {editing ? (
          <Controller
            name="priority"
            defaultValue={0}
            render={({ field }) =>
              requiredSelect({
                input: field,
                id: 'priority',
                options: priorityOptions,
                validate: [fieldRequired],
              })
            }
          />
        ) : (
          <p>
            {isNaN(Number(task?.priority ?? 0))
              ? task?.priority
              : getValueFromId(Number(task?.priority ?? 0), priorityOptions as SelectOption[])}
          </p>
        )}
      </div>
      <div className="form-group padded">
        <label>{intl.formatMessage({ id: 'item', defaultMessage: 'Item' })}</label>
        {editing ? (
          <CustomSelectInput
            options={assetSelectOptions}
            value={assetSelectValue}
            onChange={handleAssetChange}
            placeholder={intl.formatMessage({
              id: 'selectAsset',
              defaultMessage: 'Select a group or item…',
            })}
            isClearable={true}
            multi={false}
          />
        ) : (
          <p>{assetDisplayLabel ?? intl.formatMessage({ id: 'notAdded' })}</p>
        )}
      </div>
    </div>
  );
};
